"use client";

import * as React from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

export interface RoleTrackTab {
  id: string;
  label: string;
  children?: React.ReactNode;
  /** Nested segment tabs shown when this track is active (e.g. Revenue sub-tracks). */
  segments?: {
    id: string;
    label: string;
    children: React.ReactNode;
  }[];
}

export interface RoleTrackSelectorLabels {
  title: string;
  subtitle: string;
}

export interface RoleTrackSelectorProps {
  tabs: RoleTrackTab[];
  labels: RoleTrackSelectorLabels;
  className?: string;
}

export function RoleTrackSelector({
  tabs,
  labels,
  className,
}: RoleTrackSelectorProps) {
  const initialTrack = tabs[0]?.id ?? "";
  const [activeTrack, setActiveTrack] = React.useState(initialTrack);

  const activeTab = tabs.find((tab) => tab.id === activeTrack) ?? tabs[0];
  const initialSegment = activeTab?.segments?.[0]?.id ?? "";
  const [activeSegment, setActiveSegment] = React.useState(initialSegment);

  React.useEffect(() => {
    const nextTab = tabs.find((tab) => tab.id === activeTrack);
    setActiveSegment(nextTab?.segments?.[0]?.id ?? "");
  }, [activeTrack, tabs]);

  const panelContent = activeTab?.segments?.length ? (
    <Tabs value={activeSegment} onValueChange={setActiveSegment}>
      <TabsList className="border-grey-100 bg-grey-0 mb-0 flex h-auto w-full gap-2.5 rounded-none border-0 border-b px-6 py-3 lg:gap-3 lg:border lg:p-3">
        {activeTab.segments.map((segment) => (
          <TabsTrigger
            key={segment.id}
            value={segment.id}
            className="text-ds-label-md text-grey-950 rounded-ds-m data-active:bg-grey-100 data-active:border-grey-100 h-8 min-w-0 flex-1 border border-transparent bg-transparent px-2 py-2"
          >
            {segment.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {activeTab.segments.map((segment) => (
        <TabsContent key={segment.id} value={segment.id} className="mt-0">
          {segment.children}
        </TabsContent>
      ))}
    </Tabs>
  ) : (
    activeTab?.children
  );

  return (
    <section
      id="track-selector"
      className={cn(
        "bg-grey-50 border-grey-100 border-b px-6 pt-16 pb-12 md:px-12 lg:px-20 lg:pt-20 lg:pb-16",
        className
      )}
    >
      <div className="mx-auto max-w-[1280px]">
        <div className="mb-12 flex flex-col gap-3">
          <h2 className="text-ds-heading-h2 text-grey-950 text-balance">
            {labels.title}
          </h2>
          <p className="text-ds-body-md text-grey-950 max-w-[760px]">
            {labels.subtitle}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-stretch">
          {/* Desktop track nav — Figma 12161:38398 */}
          <aside className="border-grey-100 bg-grey-0 hidden w-[217px] shrink-0 flex-col self-stretch border-t border-b border-l lg:flex">
            <div className="bg-grey-0 flex flex-col gap-2 p-3">
              {tabs.map((tab) => (
                <label
                  key={tab.id}
                  className="rounded-ds-m bg-grey-0 flex h-8 cursor-pointer items-center gap-2 px-2 py-2"
                >
                  <Checkbox
                    checked={activeTrack === tab.id}
                    onCheckedChange={() => setActiveTrack(tab.id)}
                    aria-label={tab.label}
                  />
                  <span className="text-ds-label-sm text-grey-950">
                    {tab.label}
                  </span>
                </label>
              ))}
            </div>
          </aside>

          {/* Content panel — mobile track tabs Figma 12161:38480 */}
          <div className="border-grey-100 bg-grey-0 min-w-0 flex-1 border lg:border-l-0">
            <div className="relative overflow-hidden lg:hidden">
              <div className="flex h-[72px] items-center gap-2 overflow-x-auto px-6 py-5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {tabs.map((tab) => (
                  <label
                    key={tab.id}
                    className="rounded-ds-m bg-grey-50 flex h-8 shrink-0 cursor-pointer items-center gap-2 px-2 py-2"
                  >
                    <Checkbox
                      checked={activeTrack === tab.id}
                      onCheckedChange={() => setActiveTrack(tab.id)}
                      aria-label={tab.label}
                    />
                    <span className="text-ds-label-sm text-grey-950 whitespace-nowrap select-none">
                      {tab.label}
                    </span>
                  </label>
                ))}
              </div>
              <div
                aria-hidden
                className="from-grey-0 pointer-events-none absolute inset-y-0 right-0 w-[72px] bg-gradient-to-l to-transparent"
              />
            </div>
            {panelContent}
          </div>
        </div>
      </div>
    </section>
  );
}
